import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  Wrap,
  WrapItem,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FiArrowDown } from "react-icons/fi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Pagination } from "../components/Pagination";
import { PageHero } from "../components/common/PageHero";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { Footer } from "../components/landing/Footer";
import { Navbar } from "../components/landing/Navbar";
import { getNewsCategoryPath, getNewsDetailPath } from "../config/routes";
import { NewsCard } from "../components/news/NewsCard";
import { getNewsCategoryById, NEWS_CATEGORIES } from "../data/news";
import { news } from "../services/api";
import { getNewsSeoPathSegment } from "../utils/news";

const pageSize = 8;
const ALL_NEWS_CATEGORY = {
  id: "all",
  title: "همه دسته‌بندی‌ها",
  description:
    "آرشیو کامل اخبار، اطلاعیه‌ها، گزارش‌ها، انتشارات و تحلیل‌های منتشرشده را بدون فیلتر دسته‌بندی در این بخش دنبال کنید.",
  heroTitle: "آرشیو کامل اطلاع‌رسانی",
  heroSubtitle:
    "در این بخش همه اخبار و محتواهای منتشرشده تأمین سرمایه امید بدون محدودیت دسته‌بندی در دسترس قرار می‌گیرد.",
  accentColor: "brand",
};

export const NewsCategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAllCategory = category === "all";
  const activeCategory = isAllCategory
    ? ALL_NEWS_CATEGORY
    : getNewsCategoryById(category);
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const currentPage = Math.max(1, Number(searchParams.get("page") || 1));

  const { data, isLoading } = useQuery({
    queryKey: ["public-news", activeCategory?.id, currentPage],
    queryFn: () =>
      news.getPublic({
        category: isAllCategory ? undefined : activeCategory?.id,
        page: currentPage,
        pageSize,
      }),
    enabled: Boolean(activeCategory?.id),
  });

  if (!activeCategory) {
    return (
      <Box dir="rtl" overflowX="hidden">
        <Navbar />
        <Box py={40}>
          <Container maxW="container.md">
            <VStack spacing={5} textAlign="center">
              <Heading color={textColor} fontSize="2xl">
                دسته‌بندی مورد نظر یافت نشد
              </Heading>
              <Text color={subtitleColor}>
                ممکن است آدرس واردشده نامعتبر باشد یا این بخش هنوز منتشر نشده
                باشد.
              </Text>
              <Button colorScheme="brand" onClick={() => navigate("/news")}>
                بازگشت به صفحه اطلاع‌رسانی
              </Button>
            </VStack>
          </Container>
        </Box>
        <Footer />
      </Box>
    );
  }

  const items = data?.news || [];
  const totalPages = Math.max(data?.totalPage || 1, 1);

  const scrollToItems = () => {
    const el = document.getElementById("category-items");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePageChange = (page: number) => {
    const nextPage = Math.max(1, page);
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextPage === 1) {
      nextSearchParams.delete("page");
    } else {
      nextSearchParams.set("page", String(nextPage));
    }

    setSearchParams(nextSearchParams);
    window.requestAnimationFrame(scrollToItems);
  };

  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />
      <PageHero
        title={activeCategory.heroTitle}
        titleHighlight="اطلاع‌رسانی امید"
        subtitle={activeCategory.heroSubtitle}
        primaryAction={{
          label: "مشاهده محتواها",
          icon: FiArrowDown,
          onClick: scrollToItems,
        }}
      />

      <SectionWrapper>
        <Container maxW="container.xl">
          <VStack spacing={5} align="center">
            <Heading
              as="h2"
              fontSize={{ base: "lg", md: "xl" }}
              color={textColor}
            >
              دسترسی سریع به دسته‌ها
            </Heading>
            <Wrap spacing={3} justify={"center"}>
              <WrapItem key="all">
                <Button
                  variant={isAllCategory ? "solid" : "outline"}
                  colorScheme="brand"
                  onClick={() => navigate(getNewsCategoryPath("all"))}
                >
                  همه دسته‌بندی‌ها
                </Button>
              </WrapItem>
              {NEWS_CATEGORIES.map((item) => (
                <WrapItem key={item.id}>
                  <Button
                    variant={
                      item.id === activeCategory.id ? "solid" : "outline"
                    }
                    colorScheme={item.accentColor}
                    onClick={() => navigate(getNewsCategoryPath(item.id))}
                  >
                    {item.title}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          </VStack>
        </Container>
      </SectionWrapper>

      <SectionWrapper id="category-items" alt>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading as="h2" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}>
              {activeCategory.title}
            </Heading>
            <Text color={subtitleColor} maxW="720px">
              {activeCategory.description}
            </Text>
          </VStack>

          {isLoading ? (
            <HStack justify="center" py={10}>
              <Spinner size="lg" />
            </HStack>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
                {items.map((item) => (
                  <NewsCard
                    key={item._id}
                    item={item}
                    onClick={() =>
                      navigate(
                        getNewsDetailPath(
                          item.category,
                          item.slug,
                          getNewsSeoPathSegment(item),
                        ),
                      )
                    }
                  />
                ))}
              </SimpleGrid>

              {totalPages > 1 ? (
                <Box pt={8}>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </Box>
              ) : null}
            </>
          )}
        </Container>
      </SectionWrapper>

      <Footer />
    </Box>
  );
};
